import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { API_BASE, api, errorMessage } from '../lib/api';
import type { OssReport } from '../lib/types';
import { formatMoney } from '../lib/utils';

const DATASETS = ['documents', 'document_lines', 'payments', 'refunds'] as const;
const FORMATS = ['csv', 'xlsx', 'xml'] as const;

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Бухгалтерия: выгрузки за период (CSV/Excel/XML) и OSS-отчёт за квартал. */
export function AccountingPage() {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const [from, setFrom] = useState(
    iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))),
  );
  const [to, setTo] = useState(iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0))));
  const [year, setYear] = useState(now.getUTCFullYear());
  const [quarter, setQuarter] = useState(Math.floor(now.getUTCMonth() / 3) + 1);
  const oss = useQuery({
    queryKey: ['accounting', 'oss', year, quarter],
    queryFn: () =>
      api<OssReport>(`/accounting/oss?year=${String(year)}&quarter=${String(quarter)}`),
  });
  const link = (dataset: string, format: string) =>
    `${API_BASE}/accounting/export?${new URLSearchParams({ dataset, format, from, to }).toString()}`;

  return (
    <>
      <PageHeader title={t('acc.title')} description={t('acc.hint')} />
      <div className="grid gap-6">
        <Card>
          <CardHeader title={t('acc.exports')} description={t('acc.exportsHint')} />
          <CardContent className="grid gap-4 text-sm">
            <div className="flex flex-wrap gap-2">
              <Field label={t('ord.from')}>
                {(id) => (
                  <Input
                    id={id}
                    type="date"
                    value={from}
                    onChange={(e) => {
                      setFrom(e.target.value);
                    }}
                  />
                )}
              </Field>
              <Field label={t('ord.to')}>
                {(id) => (
                  <Input
                    id={id}
                    type="date"
                    value={to}
                    onChange={(e) => {
                      setTo(e.target.value);
                    }}
                  />
                )}
              </Field>
            </div>
            <table className="w-full">
              <tbody>
                {DATASETS.map((d) => (
                  <tr key={d} className="border-b last:border-0">
                    <td className="py-2 font-medium">{t(`acc.datasets.${d}`)}</td>
                    <td className="flex justify-end gap-3 py-2">
                      {FORMATS.map((f) => (
                        <a
                          key={f}
                          href={link(d, f)}
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <Download className="size-4" />
                          {f.toUpperCase()}
                        </a>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title={t('acc.oss')} description={t('acc.ossHint')} />
          <CardContent className="grid gap-3 text-sm">
            <div className="flex flex-wrap items-end gap-2">
              <Field label={t('acc.year')}>
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    value={year}
                    onChange={(e) => {
                      setYear(Number(e.target.value));
                    }}
                    className="w-28"
                  />
                )}
              </Field>
              <Field label={t('acc.quarter')}>
                {(id) => (
                  <Select
                    id={id}
                    value={quarter}
                    onChange={(e) => {
                      setQuarter(Number(e.target.value));
                    }}
                  >
                    {[1, 2, 3, 4].map((q) => (
                      <option key={q} value={q}>
                        Q{q}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <a
                href={`${API_BASE}/accounting/oss.csv?year=${String(year)}&quarter=${String(quarter)}`}
                className="mb-2 inline-flex items-center gap-1 text-primary hover:underline"
              >
                <Download className="size-4" />
                CSV
              </a>
            </div>
            {oss.error ? <Alert variant="destructive">{errorMessage(oss.error)}</Alert> : null}
            {oss.data?.rows.length === 0 ? (
              <p className="text-muted-foreground">{t('acc.ossEmpty')}</p>
            ) : (
              <table className="w-full">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-1">{t('acc.country')}</th>
                    <th className="text-right">{t('acc.rate')}</th>
                    <th className="text-right">{t('acc.base')}</th>
                    <th className="text-right">{t('ord.vat')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(oss.data?.rows ?? []).map((r) => (
                    <tr key={`${r.countryCode}-${String(r.taxRate)}`} className="border-t">
                      <td className="py-1">{r.countryCode}</td>
                      <td className="text-right">{r.taxRate}%</td>
                      <td className="text-right">
                        {formatMoney(r.netMinor, r.currency, i18n.language)}
                      </td>
                      <td className="text-right">
                        {formatMoney(r.taxMinor, r.currency, i18n.language)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
